import { Box, FormControl, FormHelperText, Typography } from '@mui/material';
import React, { FC } from 'react';

export interface IProps {
	id: string;
	legend: string | React.JSX.Element;
	description?: string | React.JSX.Element;
	hasError?: boolean;
	helperText?: string | false;
	children: React.ReactNode;
}

/**
 * A fieldset wrapper for grouped form elements (e.g. hh/mm time inputs, DLN fields etc.)
 * - Usage guidance -
 *   - Use the `legend` prop to provide a fieldset legend
 *   - Include a description for the group using the `description` prop - renders below the legend
 *   - Wrap around related form elements to group them semantically
 *   - Each individual child input should have it's own `FormControl` for registering errors
 * - Error handling guidance -
 *   - Provide error handling via the `hasError` and `helperText` props - these should be connected to the child input validation
 *   - hasError should be passed to the individual child input `FormControl`s to connect validation styling
 *   - Error text will render below the whole group
 * - Styling
 *   - Legend is styled like an individual input label
 * - Accessibility guidance -
 *   - As there are multiple child inputs expected, partial auto-synchronization is possible via `aria-describedby` and linking this with the `id` prop.
 *   - However, due to the dynamic number of child inputs expected, manual synchronization will be required for things like individual input labels (e.g. `htmlFor` attributes).
 *
 * @component
 * @param {IProps} props - Component props
 * @returns {JSX.Element} FieldGroupWrapper component
 *
 * @example
 * <FieldGroupWrapper id={id} legend={legend} description={description} hasError={childrenHaveError} helperText={errorText}>
 *     <FormControl fullWidth error={hasError}> // error state is shared
 *       <InputLabel htmlFor={`${id}-input-one`}>
 *       Input one
 *       </InputLabel>
 *       <Select
 *         inputProps={{ id: `${id}-input-one` }}
 *         aria-describedby={`${id}-description`} // links to the fieldset description (optional)
 * 		   {...rest}
 *       >
 *        //...input one options
 *       </Select>
 *     </FormControl>
 *		// Input two
 *     <FormControl error={hasError} fullWidth> // error state is shared
 *       <InputLabel htmlFor={`${id}-input-two`}>
 *       Input two
 *       </InputLabel>
 *       <Select
 *         inputProps={{ id: `${id}-input-two` }}
 *         aria-describedby={`${id}-description`} // links to the fieldset description (optional)
 * 		   {...rest}
 *       >
 *         // ...input two options
 *       </Select>
 *     </FormControl>
 *     // error text will render here if provided and hasError is true
 * </FieldGroupWrapper>
 */

const FieldGroupWrapper: FC<IProps> = ({ id, legend, description, hasError, helperText, children }) => {
	return (
		<FormControl component='fieldset' error={hasError} fullWidth>
			<Box component='legend' sx={{ fontWeight: 600, mb: description ? 0 : '1rem', color: '#000' }}>
				{legend}
			</Box>
			{description && (
				<Typography id={`${id ?? ''}-description`} sx={{ mb: '1rem' }}>
					{description}
				</Typography>
			)}
			<Box>{children}</Box>
			{hasError && helperText && (
				<Box sx={{ mb: '24px' }}>
					<FormHelperText
						id={`${id ?? ''}-helper-text`}
						sx={{ mx: 0, mt: '8px', fontSize: '16px', lineHeight: '24px' }}
					>
						{helperText}
					</FormHelperText>
				</Box>
			)}
		</FormControl>
	);
};

export default FieldGroupWrapper;
